import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ViewportScroller } from '@angular/common';
import {
  SharedFacade,
  customDecode,
  customEncode,
  addFullStop,
  EolNavigationService,
} from '@piqet-angular/data';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';
import { ToastrService } from 'ngx-toastr';
import {
  commonEnum,
  FeatureCodes,
  ImpactCategoriesEnum,
  materialCircularityIndex,
  PEF_NORMALIZED,
  PEF_WEIGHTED,
  ReportTypeEnum,
} from 'libs/shared/src/lib/constants';
import {
  isFeatureEnabled,
  showFeatureAlert,
  validateFeatures,
  FeatureValidation,
} from '../../../../../../../shared/src/lib/service/helper.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { JoyrideService } from 'ngx-joyride';
import {
  BAR_SECTIONS,
  GenerateChartReportData,
  GenerateChartReportRequest,
  PEF_SECTIONS,
  SPIDER_SECTIONS,
} from './chart-report.model';
import { ChartReportFormChartType, ChartReportFormData } from './chart-report-form.model';
import {
  pickContribution,
  toLegacyComponentItems,
  toLegacyPackagingComponentItems,
  toLegacyPEFCategoryItems,
  toLegacyScenarioData,
  toLegacySpiderScenarioList,
} from './chart-report.adapter';

/**
 * "Chart at a glance" on the unified `Report/GenerateChartReport` endpoint.
 *
 * Functionally identical to `ProjectReportChartGlanceComponent` — same form,
 * same validation, same charts, same gauges — but one request replaces the
 * `BarChartNew` / `SpiderChartNew` pair, and every value is read from a record
 * that carries its own scenario / indicator / bucket instead of being split out
 * of a `;`-joined string by position. See `chart-report.adapter.ts` for the
 * mapping onto the (unchanged) shared chart components.
 *
 * The old component is left untouched and still serves `chart-at-glance`; this
 * one is reached from the "Open new version" link in that screen's header, so
 * the two can be diffed side by side.
 *
 * Every path here — bar, spider and PEF categories — goes through
 * `GenerateChartReport`. No legacy endpoint is called.
 */
@Component({
  selector: 'piqet-angular-project-report-chart-glance-v2',
  templateUrl: './project-report-chart-glance-v2.component.html',
  styleUrls: ['./project-report-chart-glance-v2.component.scss'],
})
export class ProjectReportChartGlanceV2Component {
  FeatureCodes = FeatureCodes;
  CHART_COMPARE_DESCRIPTION_TO_FEATURE: Record<string, FeatureCodes> = {
    'Life cycle stage': FeatureCodes.REPORTS_LIFE_CYCLE_STAGE,
    'Packaging Level': FeatureCodes.REPORTS_PACKAGING_LEVEL,
    'Packaging components': FeatureCodes.REPORTS_PACKAGING_COMPONENTS,
    'Product loss': FeatureCodes.REPORTS_PRODUCT_LOSS,
    Ingredients: FeatureCodes.REPORTS_INGREDIENTS,
    Transport: FeatureCodes.REPORTS_TRANSPORT,
    'PEF categories (with toxicity)': FeatureCodes.REPORTS_PEF_CATEGORIES_WITH_TOXICITY,
    'PEF categories (without toxicity)': FeatureCodes.REPORTS_PEF_CATEGORIES_WITHOUT_TOXICITY,
  };
  @Input() headerBtn: any;
  chartOpacity: any = { value: commonEnum.opacity };
  opacity: any = 0.5;
  ReportTypeEnum = ReportTypeEnum;
  reportTypeId: any;
  getchartImpactCategoryIdData: any;
  /** Full chart-type list from the form data; `chartTypeItems` is this, filtered. */
  chartTypes: ChartReportFormChartType[] = [];
  defaultScenarioIds = '';
  defaultIndicatorIds = '';
  isBarChartSelected = true;
  isTypeSelected: any;
  isEquivalentFunctionUnitSelected = false;
  componentItemsArray: any = [];
  topIndicatorName!: string;
  isCustomInclude = false;
  generatedInterpretation = false;
  setChartTypeId = commonEnum.barChartOption;
  selectedIndicator: any;
  reportChartForm!: FormGroup;
  interpretationForm!: FormGroup;
  inValidScenarios = [];
  showIncorrectEOLNotification = false;
  incorrectValueMessage =
    'Report results may be inaccurate due to outdated EOL rates in the project. Please update them before generating results.';
  incorrectBtnName = 'Show scenarios';
  generatedChartType = 'column';
  isChartNormalized = false;
  isCheckInterpretationDisabled = false;
  generated = false;
  selectedScenarios: any;
  materialCircularityIndex = materialCircularityIndex;
  selectedCatogories: any;
  functionalUnitId: any;
  chartTypeId: any;
  indicatorId: any;
  impactCatogaryId: any;
  chartTypeItemsData: any;
  packagingComponentItems: any = [];
  packagingComponent1: any;
  packagingComponent2: any;
  packagingComponent3: any;
  packagingComponentName1: any;
  packagingComponentName2: any;
  packagingComponentName3: any;
  pointers: any = [{ value: 0, color: '#9747ff', length: 1 }];
  pointers1: any = [{ value: 0, color: '#9747ff', length: 1 }];
  pointers2: any = [{ value: 0, color: '#9747ff', length: 1 }];
  pointersArray: any = [];
  gaugeLegends: any = [];
  flagName: any;
  chartTypeItems: any;
  chartImpactCategoryItems: any;
  chartCompareItems: any;
  productPackageScenarioItems: any;
  functionalUnitItems: any;
  indicatorItems: any;
  indicatorsName: any;
  interpretation: any;
  selectComparisionData: any;
  selectedFunctionalUnit: any;
  selectCategoryIncludeData: any;
  activatedRouteProjectId: any;
  speedosImpactCategoryItems: any = [];
  selectedPEFType = 'Normalized';
  /** PEF Normalized/Weighted -> adapted chart rows; both filled from one call. */
  private pefCategoryItems: Record<string, any[]> = {};
  isPEFResults = false;
  /**
   * Fixed footprint for the chart on this screen. Passed as both the base and
   * the ceiling so a PEF breakdown (16 long indicator labels + legend) renders
   * in the same box as a life-cycle-stage chart (4 short labels) — otherwise
   * the taller chart pushes the gauges and the interpretation off-screen.
   */
  readonly glanceChartHeight = 550;
  incorrectFillingValueMessage = 'Action needed: Please update the filling country.';
  incorrectFillingBtnName = 'Go to filling';
  incorrectFilling: any = [];
  showIncorrectFillingNotification = false;
  /** Engine versions; always shown, never filtered by chart type. */
  versionList: any[] = [];
  selectedVersion: any = null;

  constructor(
    private projectService: ProjectsService,
    private formBuilder: FormBuilder,
    private router: Router,
    private sharedFacade: SharedFacade,
    private reportService: ReportsService,
    private eolNavService: EolNavigationService,
    private sharedService: SharedService,
    public route: ActivatedRoute,
    private toaster: ToastrService,
    private viewportScroller: ViewportScroller,
    private swalService: SwalService,
    private joyrideService: JoyrideService
  ) {
    this.sharedService.scenarioNameFunc('Chart at a glance');
    this.route.queryParams.subscribe((data: any) => {
      this.flagName = Boolean(data['flagName']);
    });
    this.activatedRouteProjectId = this.getProjectId();
  }

  async ngOnInit() {
    !this.joyrideService.isTourInProgress() && this.viewportScroller.scrollToPosition([0, 0]);
    this.isCheckInterpretationDisabled = !isFeatureEnabled(
      FeatureCodes.REPORTS_INTERPRETATION_SECTION
    );

    await this.initFormValue();
    await this.loadFormData();
    this.setFormValue();
  }

  private getProjectId(): number {
    return Number(atob(decodeURIComponent(customDecode(location.href.split('/')[6]))));
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }

  /** Single round trip: every dropdown, both banners and all saved defaults. */
  async loadFormData() {
    const response = await this.projectService.getChartReportFormData(this.getProjectId());
    const data = (response?.data ?? {}) as ChartReportFormData;

    this.productPackageScenarioItems = data.scenarios ?? [];
    this.indicatorItems = data.indicators ?? [];
    this.functionalUnitItems = data.functionalUnits ?? [];
    this.chartTypes = data.chartTypes ?? [];
    this.interpretation = data.interpretation;

    // Legacy `invalidEOLRates` was exactly `invalidScenarioList.Any()`, so the
    // flag is derived rather than carried.
    this.inValidScenarios = (data.invalidEolScenarios ?? []) as any;
    this.showIncorrectEOLNotification = this.inValidScenarios.length > 0;
    this.incorrectBtnName = this.inValidScenarios.length > 1 ? 'Show scenarios' : 'Go to scenario';

    this.incorrectFilling = data.incorrectFillingScenarios ?? [];
    this.showIncorrectFillingNotification = this.incorrectFilling.length > 0;
    this.incorrectFillingBtnName =
      this.incorrectFilling.length > 1 ? 'Show scenarios' : 'Go to filling';

    // Default to whichever version the backend flagged as selected, falling back
    // to the first one — the dropdown is always visible and not clearable, so it
    // should never sit empty while the list has options.
    this.versionList = data.versionList ?? [];
    this.selectedVersion =
      this.versionList.find((item: any) => item.selected) ?? this.versionList[0] ?? null;

    const defaults = data.defaults ?? {};
    this.defaultScenarioIds = defaults.scenarioIds ?? '';
    this.defaultIndicatorIds = defaults.indicatorIds ?? '';

    const preselected = this.defaultScenarioIds.split(',').filter(Boolean);
    this.selectedScenarios = this.productPackageScenarioItems.filter((item: any) =>
      preselected.includes(item.idStr)
    );

    this.indexChartTypeChildren();
    this.refreshChartTypeItems();

    // `chartTypeId` is nullable — null means never saved, so no preselection.
    this.chartTypeItemsData =
      this.chartTypeItems.find((item: any) => item.id === defaults.chartTypeId) ?? null;
    const activeChartType = this.chartTypeItemsData ?? this.chartTypeItems[0] ?? null;
    this.chartTypeId = activeChartType?.id ?? commonEnum.barChartOption;
    this.isBarChartSelected = this.chartTypeId === commonEnum.barChartOption;

    this.selectedIndicator =
      this.indicatorItems.find((item: any) => item.id === defaults.indicatorId) ?? null;
    this.indicatorId = this.selectedIndicator?.id ?? this.indicatorItems[0]?.id;

    // Last generated selection wins, falling back to the first option when the
    // user has never generated on this project.
    this.selectedFunctionalUnit =
      this.functionalUnitItems.find((item: any) => item.id === defaults.functionalUnitId) ??
      this.functionalUnitItems[0] ??
      null;
    this.functionalUnitId = this.selectedFunctionalUnit?.id;

    this.selectComparisionData =
      this.chartCompareItems.find((item: any) => item.id === defaults.reportTypeId) ??
      this.chartCompareItems[0] ??
      null;
    this.reportTypeId = this.selectComparisionData?.id;

    this.resolveImpactCategory(defaults.impactCategoryId);
  }

  /**
   * Comparisons hang off the bar chart type and impact categories off the
   * spider one. The template only ever shows one of the two — bound to
   * `isBarChartSelected` — so both lists are lifted once here rather than being
   * re-read on every chart-type change.
   */
  private indexChartTypeChildren() {
    const comparisons =
      this.chartTypes.find((type: any) => type.comparisons?.length)?.comparisons ?? [];
    this.chartCompareItems = comparisons.map((item: any) => {
      const description = item.description || item.name;
      const featureCode = this.CHART_COMPARE_DESCRIPTION_TO_FEATURE[description];
      return {
        ...item,
        description,
        disabled: featureCode ? !isFeatureEnabled(featureCode) : false,
      };
    });
    this.chartImpactCategoryItems =
      this.chartTypes.find((type: any) => type.impactCategories?.length)?.impactCategories ?? [];
  }

  /**
   * Chart-type availability is a pure function of the selected scenario count,
   * so the list is filtered locally instead of re-fetched on every selection
   * change. Floored at 1 so clearing the selection still offers the bar chart
   * rather than emptying the dropdown.
   */
  private refreshChartTypeItems() {
    const count = Math.max(1, this.selectedScenarios?.length ?? 0);
    this.chartTypeItems = this.chartTypes.filter((type: any) => count >= (type.minScenarios ?? 1));
  }

  private resolveImpactCategory(impactCategoryId: any) {
    const match = this.chartImpactCategoryItems.find((item: any) => item.id === impactCategoryId);
    if (!match) return;
    this.getchartImpactCategoryIdData = match;
    this.impactCatogaryId = match.id;
    this.isCustomInclude = match.name == 'Customize categories';
    if (this.isCustomInclude) {
      this.reportChartForm.controls['categoryInclude'].setValidators([Validators.required]);
    } else {
      this.reportChartForm.controls['categoryInclude'].clearValidators();
    }
    this.reportChartForm.controls['categoryInclude'].updateValueAndValidity();
  }

  async initFormValue() {
    this.reportChartForm = this.formBuilder.group({
      scenarios: [null, Validators.required],
      chartType: [null, Validators.required],
      impactCategory: [null],
      comparision: [null],
      categoryInclude: [null],
      functionalUnit: [null, Validators.required],
      baselineFunctional: [null],
      version: [null],
    });
    this.interpretationForm = this.formBuilder.group({
      interpretation: ['', Validators.required],
    });
  }

  getMatchingScenarioIds(dataArray: any, fullReportScenarioIds: any) {
    const matchingIds: any = [];
    dataArray.forEach((item: any) => {
      if (fullReportScenarioIds?.includes(item.idStr)) {
        matchingIds.push(item.idStr);
      }
    });
    return matchingIds;
  }

  setFormValue() {
    const indicatorsArray = this.defaultIndicatorIds?.split(',')?.map(Number);
    const filteredRecords = this.indicatorItems?.filter((record: any) =>
      indicatorsArray?.includes(record.id)
    );
    const scenarioBindIds = this.getMatchingScenarioIds(
      this.productPackageScenarioItems,
      this.defaultScenarioIds?.split(',')
    );
    this.reportChartForm.patchValue({
      chartType: this.chartTypeItemsData || (this.chartTypeItems ? this.chartTypeItems[0] : null),
      impactCategory: this.getchartImpactCategoryIdData,
      comparision: this.selectComparisionData ?? this.chartCompareItems[0],
      scenarios: scenarioBindIds,
      functionalUnit: this.selectedFunctionalUnit ?? this.functionalUnitItems[0],
      version: this.selectedVersion,
    });
    !this.isBarChartSelected && this.isCustomInclude
      ? this.reportChartForm.patchValue({ categoryInclude: filteredRecords })
      : this.reportChartForm.patchValue({
          categoryInclude: this.selectedIndicator || this.indicatorItems[0],
        });
    this.interpretationForm.patchValue({ interpretation: this.interpretation });
    this.isTypeSelected = this.chartTypeItemsData ? true : false;
  }

  compareFn(item1: any, item2: any): boolean {
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  }

  chartType(event: any) {
    if (event?.id) {
      this.isBarChartSelected = event.id === 2 ? true : false;
      this.isTypeSelected = true;
      if (this.isBarChartSelected) {
        this.reportChartForm.patchValue({
          categoryInclude: this.selectCategoryIncludeData
            ? this.selectCategoryIncludeData
            : this.selectedIndicator,
          impactCategory: this.selectCategoryIncludeData,
          comparision: this.selectComparisionData
            ? this.selectComparisionData
            : this.chartCompareItems[0],
        });
        this.reportChartForm.controls['comparision'].setValidators([Validators.required]);
        this.reportChartForm.controls['impactCategory'].clearValidators();
      } else {
        this.reportChartForm.controls['impactCategory'].reset();
        this.reportChartForm.patchValue({ impactCategory: this.getchartImpactCategoryIdData });
        this.reportChartForm.controls['impactCategory'].setValidators([Validators.required]);
        this.reportChartForm.controls['categoryInclude'].setValue(null);
        this.reportChartForm.controls['comparision'].clearValidators();
      }
      this.reportChartForm.controls['comparision'].updateValueAndValidity();
      this.reportChartForm.controls['impactCategory'].updateValueAndValidity();
      this.chartTypeId = event.id;
    }
  }

  functionalUnit(event: any) {
    if (!event.id) return;
    this.isEquivalentFunctionUnitSelected = event.id === 3 ? true : false;
    if (this.isEquivalentFunctionUnitSelected) {
      this.reportChartForm.controls['baselineFunctional'].setValidators([Validators.required]);
    } else {
      this.reportChartForm.controls['baselineFunctional'].clearValidators();
    }
    this.reportChartForm.controls['baselineFunctional'].updateValueAndValidity();
    this.functionalUnitId = event.id;
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchDis(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  scenarioSelected(event: any) {
    // Guard against a stray DOM `change` bubbling out of the header search
    // input: it is not a selection, and treating it as one would clear the
    // scenarios. The template stops propagation; this is the second line.
    if (!Array.isArray(event)) return;
    this.reportChartForm.controls['baselineFunctional'].setValue(null);
    if (!event.length) {
      this.selectedScenarios = null;
      this.refreshChartTypeItems();
      return;
    }
    this.selectedScenarios = event;
    this.refreshChartTypeItems();
    if (this.selectedScenarios.length == 1 && this.isTypeSelected) {
      this.reportChartForm.patchValue({ chartType: this.chartTypeItems[0] });
      this.reportChartForm.controls['categoryInclude'].setValue(null);
      this.isBarChartSelected = true;
      this.chartTypeId = 2;
    }
  }

  selectedCategory(event: any) {
    if (!event.id) return;
    this.indicatorId = event.id;
    this.selectCategoryIncludeData = event;
  }

  catogorySelected(event: any) {
    if (!event.length) return;
    this.selectedCatogories = event;
  }

  getImpactCatogaryId(data: any) {
    if (data.id) {
      this.isPEFResults = false;
      this.getchartImpactCategoryIdData = data;
      this.isCustomInclude = data.name == 'Customize categories' ? true : false;
      if (data.name == 'Customize categories') {
        this.reportChartForm.controls['categoryInclude'].setValidators([Validators.required]);
      } else {
        this.reportChartForm.controls['categoryInclude'].clearValidators();
      }
      this.reportChartForm.controls['categoryInclude'].updateValueAndValidity();
      this.impactCatogaryId = data.id;
    }
  }

  /**
   * `comparisons[].id` is already a `ReportTypeEnum` value, so it goes straight
   * onto the payload — the old `ChartCompareTypeEnum` 1-8 -> report-type
   * translation table is gone.
   */
  getChartCompareId(event: any) {
    if (!event?.id) return;
    this.isChartNormalized = false;
    this.generatedChartType = 'column';
    this.selectComparisionData = event;
    this.reportTypeId = event.id;

    // PEF is the one dimension that also pins an impact category: the weighted
    // numbers pick their toxicity multiplier from it.
    if (event.id === ReportTypeEnum.PEF_Categories_tox) {
      this.impactCatogaryId = ImpactCategoriesEnum.PEFCategoriesWithTOX;
      this.updateValidationForPEFCategories();
    } else if (event.id === ReportTypeEnum.PEF_Categories_without_tox) {
      this.impactCatogaryId = ImpactCategoriesEnum.PEFCategoriesWithoutTOX;
      this.updateValidationForPEFCategories();
    }
  }

  updateValidationForPEFCategories() {
    this.reportChartForm.controls['categoryInclude'].clearValidators();
    this.reportChartForm.controls['categoryInclude'].updateValueAndValidity();
  }

  private validateReportFeatures(): string[] {
    const formValues = this.reportChartForm.value;
    const validations: FeatureValidation[] = [];

    if (this.isBarChartSelected && formValues.comparision) {
      const comparisonName = formValues.comparision.description;
      const featureCode = this.CHART_COMPARE_DESCRIPTION_TO_FEATURE[comparisonName];

      if (featureCode && !isFeatureEnabled(featureCode)) {
        validations.push({
          condition: true,
          featureCode: featureCode,
          featureName: comparisonName,
        });
      }
    }

    if (this.reportTypeId === ReportTypeEnum.PEF_Categories_tox) {
      validations.push({
        condition: true,
        featureCode: FeatureCodes.REPORTS_PEF_CATEGORIES_WITH_TOXICITY,
        featureName: 'PEF categories (with toxicity)',
      });
    }

    if (this.reportTypeId === ReportTypeEnum.PEF_Categories_without_tox) {
      validations.push({
        condition: true,
        featureCode: FeatureCodes.REPORTS_PEF_CATEGORIES_WITHOUT_TOXICITY,
        featureName: 'PEF categories (without toxicity)',
      });
    }

    return validateFeatures(validations, isFeatureEnabled);
  }

  /** True when the PEF Normalized/Weighted breakdown is being requested. */
  private get isPEFComparison(): boolean {
    return (
      this.isBarChartSelected &&
      (this.reportTypeId == ReportTypeEnum.PEF_Categories_tox ||
        this.reportTypeId == ReportTypeEnum.PEF_Categories_without_tox)
    );
  }

  onGenerate() {
    this.generated = true;
    if (this.reportChartForm.invalid) {
      return;
    }
    const missingFeatures = this.validateReportFeatures();
    if (missingFeatures.length > 0) {
      showFeatureAlert(missingFeatures, this.swalService);
      return;
    }

    this.componentItemsArray = [];
    this.packagingComponentItems = [];
    this.speedosImpactCategoryItems = [];
    this.sharedService.show('');

    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    const isSpider = this.chartTypeId == 3;
    this.setChartTypeId = this.chartTypeId || 2;

    if (this.isPEFComparison) {
      this.loadPEFReport();
      return;
    }

    const payload = this.buildChartReportPayload(isSpider);
    this.projectService.generateChartReport(payload).subscribe((res: any) => {
      if (!res?.data) {
        this.sharedService.show('');
        return;
      }
      isSpider ? this.applySpiderResponse(res.data) : this.applyBarResponse(res.data);
      this.sharedService.show('');
    });
  }

  /**
   * Same `GetChartViewModel` body both legacy endpoints took — kept field for
   * field so the indicator-selection rules resolve exactly as before — plus the
   * `sections` bit flags that let one call cover the chart, the gauge and the
   * scenario totals.
   */
  private buildChartReportPayload(isSpider: boolean): GenerateChartReportRequest {
    const base = {
      projectId: this.getProjectId(),
      scenarioIds: this.selectedScenarioIds(),
      baseScenarioId: this.selectedBaseScenarioId(),
      chartTypeId: this.chartTypeId || commonEnum.barChartOption,
      functionalUnitId: this.functionalUnitId || 1,
      isExcludeProductImpacts: false,
      isExport: false,
      version: this.selectedVersionId(),
    };

    return isSpider
      ? {
          ...base,
          // No contribution dimension on a spider chart. Sending the last
          // comparison the user picked would be actively wrong now that 21 / 22
          // mean "give me the PEF breakdown instead".
          reportTypeId: 0,
          impactCategoryId: this.impactCatogaryId || 0,
          // Only "Customize categories" pins an explicit indicator set; for any
          // other category the set must be derived from `impactCategoryId`, so
          // this has to stay empty or it would override the user's choice.
          indicatorIds: this.isCustomInclude
            ? this.selectedCatogories?.map((x: any) => x.id).join(',') || this.defaultIndicatorIds
            : '',
          sections: SPIDER_SECTIONS,
        }
      : {
          ...base,
          reportTypeId: this.reportTypeId || 0,
          impactCategoryId: this.impactCatogaryId || 0,
          // A bar chart is always one indicator, and naming it explicitly is
          // what the old `chartImpactCategoryId: 7` override existed to do.
          indicatorIds: String(this.indicatorId ?? ''),
          sections: BAR_SECTIONS,
        };
  }

  /** csv of the selected scenarios, falling back to the saved default. */
  private selectedScenarioIds(): string {
    return this.selectedScenarios?.map((x: any) => x.idStr).join(',') || this.defaultScenarioIds;
  }

  /**
   * Baseline for functional comparison. Read from the control rather than the
   * `baselineValue` handler's copy, which goes stale when a scenario change
   * resets the control.
   */
  private selectedBaseScenarioId(): number {
    return Number(this.reportChartForm.controls['baselineFunctional'].value?.idStr) || 0;
  }

  /** Selected engine version, or null when the backend shipped no versions. */
  private selectedVersionId(): number | null {
    return this.reportChartForm.controls['version'].value?.id ?? null;
  }

  private applyBarResponse(data: GenerateChartReportData) {
    this.isPEFResults = false;
    this.generatedChartType = 'column';
    this.applyCommonResponse(data);

    const contribution = pickContribution(data, this.indicatorId);
    this.componentItemsArray = toLegacyComponentItems(contribution, data.scenarios);
    // The engine writes the full axis caption to the chart itself, so the glance
    // chart and GenerateCustomReport label their y-axis identically instead of
    // each composing one. `indicatorUnitName` is the fallback.
    this.indicatorsName =
      contribution?.chart?.yAxis?.label ?? contribution?.indicatorUnitName ?? '';
    this.getTopIndicatorName(data.meta?.reportTypeId);
    this.generated = true;
  }

  private applySpiderResponse(data: GenerateChartReportData) {
    this.isPEFResults = false;
    this.applyCommonResponse(data);

    this.componentItemsArray = toLegacySpiderScenarioList(data);
    this.generated = true;
  }

  /** Sections every chart type on this screen requests: opacity + the gauge. */
  private applyCommonResponse(data: GenerateChartReportData) {
    this.opacity = data.meta?.opacity ?? 0.5;
    this.chartOpacity = { value: this.opacity };
    this.speedosImpactCategoryItems = data.speedosImpactCategoryItems ?? [];
    this.generateIndicators(
      toLegacyPackagingComponentItems(data),
      toLegacyScenarioData(data.scenarios)
    );
  }

  // ── PEF categories ──────────────────────────────────────────────────────
  // The `pef` section carries the normalized and the weighted breakdown
  // together, so — as with the old endpoint — one call serves both toggle
  // states and switching between them costs nothing.

  private loadPEFReport() {
    this.projectService.generateChartReport(this.buildPEFPayload()).subscribe((res: any) => {
      if (!res?.data) {
        this.sharedService.show('');
        return;
      }
      const data = res.data as GenerateChartReportData;
      this.applyCommonResponse(data);
      this.pefCategoryItems = {
        Normalized: toLegacyPEFCategoryItems(data.pef?.normalized, 'scenario'),
        Weighted: toLegacyPEFCategoryItems(data.pef?.weighted, 'indicator'),
      };
      this.indicatorsName = 'Fraction of global impact per person';
      this.isPEFResults = true;
      this.applyPEFView('Normalized');
      this.generated = true;
      this.sharedService.show('');
    });
  }

  /**
   * `reportTypeId` 21 / 22 is what selects the PEF breakdown — the old
   * `isPEFCategoriesResult` flag is gone because the selection already says so.
   *
   * `impactCategoryId` must stay the one the user picked (17 / 18): the weighted
   * numbers take their toxicity multiplier from it, so a `_Weighted` category
   * would silently apply the without-toxics multiplier to a with-toxics report.
   *
   * `indicatorIds` stays empty so the whole PEF set is derived from the impact
   * category — naming one indicator would collapse the breakdown to a single bar.
   */
  private buildPEFPayload(): GenerateChartReportRequest {
    return {
      projectId: this.getProjectId(),
      scenarioIds: this.selectedScenarioIds(),
      baseScenarioId: this.selectedBaseScenarioId(),
      chartTypeId: this.chartTypeId || commonEnum.barChartOption,
      reportTypeId: this.reportTypeId,
      impactCategoryId: this.impactCatogaryId,
      indicatorIds: '',
      functionalUnitId: this.functionalUnitId || 1,
      isExcludeProductImpacts: false,
      isExport: false,
      version: this.selectedVersionId(),
      sections: PEF_SECTIONS,
    };
  }

  private applyPEFView(type: string) {
    this.selectedPEFType = type;
    this.componentItemsArray = this.pefCategoryItems[type] ?? [];
    this.chartOpacity = { value: this.opacity };
    if (type === 'Weighted') {
      this.topIndicatorName = PEF_WEIGHTED;
      this.generatedChartType = 'bar';
      this.isChartNormalized = false;
    } else {
      this.topIndicatorName = PEF_NORMALIZED;
      this.generatedChartType = 'column';
      this.isChartNormalized = true;
    }
  }

  changePEFCategories(type: string) {
    this.applyPEFView(type);
  }

  onSaveInterPretation() {
    this.generatedInterpretation = true;
    if (this.interpretationForm.controls['interpretation'].invalid) {
      return;
    }
    this.reportService
      .saveInterpretation(
        this.getProjectId(),
        addFullStop(this.interpretationForm.controls['interpretation'].value)
      )
      .subscribe((data: any) => {
        if (data.messageType == 0) {
          this.toaster.success(data.message);
        } else {
          this.toaster.error(data.error);
        }
      });
  }

  // ── Gauge ───────────────────────────────────────────────────────────────
  // `packagingComponentItems` arrives grouped by gauge row (`componentId`),
  // one entry per scenario within each group. Groups are looked up by id
  // rather than by array position — the engine now emits rows 17-21 too, so
  // relying on `pointersArray[id - 1]` would only hold while the row set
  // happened to be dense.

  generateIndicators(data: any, scenarioData: any) {
    this.packagingComponentItems = data ?? [];
    if (!this.packagingComponentItems.length) {
      this.pointersArray = [];
      this.gaugeLegends = [];
      return;
    }

    const groupedRecords = this.groupRecordsByProperty(this.packagingComponentItems, 'componentId');
    this.pointersArray = Object.keys(groupedRecords).map(key => ({
      el: this.setPointers(groupedRecords[key], scenarioData),
      id: Number(key),
    }));

    this.gaugeLegends = [...(this.pointersArray[0]?.el ?? [])];

    const selectedCategories = this.speedosImpactCategoryItems.filter((x: any) => x.selected);
    const defaultCategory = this.speedosImpactCategoryItems[0];
    const [preferred1, preferred2, preferred3] = selectedCategories;

    this.packagingComponent1 = preferred1?.id ?? defaultCategory?.id;
    this.packagingComponent2 = preferred2?.id ?? defaultCategory?.id;
    this.packagingComponent3 = preferred3?.id ?? defaultCategory?.id;

    this.packagingComponentName1 = preferred1?.name ?? defaultCategory?.name;
    this.packagingComponentName2 = preferred2?.name ?? defaultCategory?.name;
    this.packagingComponentName3 = preferred3?.name ?? defaultCategory?.name;

    this.pointers = this.prepareGaugeData(this.getPointerGroup(this.packagingComponent1));
    this.pointers1 = this.prepareGaugeData(this.getPointerGroup(this.packagingComponent2));
    this.pointers2 = this.prepareGaugeData(this.getPointerGroup(this.packagingComponent3));
  }

  /** Gauge-row entries for a metric id, or the first group when it is missing. */
  private getPointerGroup(componentId: any): any[] {
    const group = this.pointersArray.find((x: any) => x.id == componentId) ?? this.pointersArray[0];
    return [...(group?.el ?? [])];
  }

  setPointers(pointerArray: any, scenarioData: any) {
    pointerArray = this.getCurrentPointer(pointerArray);
    return pointerArray.map((element: any, index: any) => ({
      ...element,
      index: index,
      label: scenarioData[index]?.shortScenarioName || scenarioData[index]?.name,
      hideLegends: false,
    }));
  }

  prepareGaugeData(pointers: any[]): any[] {
    return pointers.map(pointer => ({
      ...pointer,
      value: pointer.value || 0,
      color: pointer.color || '#9747ff',
      label: pointer.label || '',
      formatedValue: pointer.formatedValue || `${pointer.value}%`,
    }));
  }

  getTopIndicatorName(data: any) {
    switch (data) {
      case 14:
        this.topIndicatorName = 'Contribution by life cycle stages';
        break;
      case 16:
        this.topIndicatorName = 'Contribution by packaging levels';
        break;
      case 18:
        this.topIndicatorName = 'Contribution by packaging components';
        break;
      case 17:
        this.topIndicatorName = 'Contribution by product waste';
        break;
      case 19:
        this.topIndicatorName = 'Contribution by ingredients';
        break;
      case 20:
        this.topIndicatorName = 'Contribution of transport';
        break;
      default:
        break;
    }
  }

  changePackagingComponent(value: any, indicatorNo: number) {
    // Without this, a stray DOM `change` from the header search input writes
    // `undefined` into the gauge's ngModel, which makes ng-select clear its
    // selection and render an empty box. Metric ids are 1-based, so a falsy
    // id is never a real selection.
    if (!value?.id) return;
    const group = this.getPointerGroup(value.id);
    group.forEach((ele: any) => {
      const hidden = this.gaugeLegends
        .filter((x: any) => x.hideLegends)
        .filter((y: any) => y.color == ele.color && y.label == ele.label);
      ele.hideLegends = hidden.length ? hidden[0].hideLegends : false;
    });
    this.gaugeLegends = [...group];

    const visible = group.filter((x: any) => !x.hideLegends);
    const name =
      this.speedosImpactCategoryItems.find((x: any) => x.id == value.id)?.name ?? value.name;

    if (indicatorNo == 1) {
      this.pointers = this.pointers.length ? visible : this.pointers;
      this.packagingComponent1 = value.id;
      this.packagingComponentName1 = name;
    } else if (indicatorNo == 2) {
      this.pointers1 = this.pointers1.length ? visible : this.pointers1;
      this.packagingComponent2 = value.id;
      this.packagingComponentName2 = name;
    } else {
      this.pointers2 = this.pointers2.length ? visible : this.pointers2;
      this.packagingComponent3 = value.id;
      this.packagingComponentName3 = name;
    }
  }

  groupRecordsByProperty(records: any[], property: string): any {
    const groupedRecords: any = {};
    records.forEach(record => {
      const value = record[property];
      if (groupedRecords[value]) {
        groupedRecords[value].push(record);
      } else {
        groupedRecords[value] = [record];
      }
    });
    return groupedRecords;
  }

  getCurrentPointer(array: any) {
    array.forEach((x: any, index: any) => {
      x['length'] = 1;
      x['color'] =
        index + 1 == 1
          ? 'rgba(151,71,255,1)'
          : index + 1 == 2
          ? 'rgba(251,59,255,1)'
          : index + 1 == 3
          ? 'rgba(255,46,52,1)'
          : index + 1 == 4
          ? 'rgba(255,151,20,1)'
          : index + 1 == 5
          ? 'rgba(20,232,5,1)'
          : 'rgba(5,232,250,1)';
    });
    return array;
  }

  hidePointer(pointer: any) {
    const legend = this.gaugeLegends.filter(
      (x: any) => x.color == pointer.color && x.label == pointer.label
    )[0];
    if (!legend) return;
    const hideLegends = !legend.hideLegends;
    legend.hideLegends = hideLegends;

    const index = this.pointers.findIndex(
      (x: any) => x.color == pointer.color && x.label == pointer.label
    );
    const index1 = this.pointers1.findIndex(
      (x: any) => x.color == pointer.color && x.label == pointer.label
    );
    const index2 = this.pointers2.findIndex(
      (x: any) => x.color == pointer.color && x.label == pointer.label
    );
    if (hideLegends) {
      this.pointers.splice(index, 1);
      this.pointers1.splice(index1, 1);
      this.pointers2.splice(index2, 1);
    } else {
      const matches = (x: any) => x.color == pointer.color && x.label == pointer.label;
      const pointer1 = this.getPointerGroup(this.packagingComponent1).find(matches);
      const pointer2 = this.getPointerGroup(this.packagingComponent2).find(matches);
      const pointer3 = this.getPointerGroup(this.packagingComponent3).find(matches);

      if (pointer1) this.pointers.push(this.prepareGaugeData([pointer1])[0]);
      if (pointer2) this.pointers1.push(this.prepareGaugeData([pointer2])[0]);
      if (pointer3) this.pointers2.push(this.prepareGaugeData([pointer3])[0]);
    }

    this.pointers = [...this.pointers];
    this.pointers1 = [...this.pointers1];
    this.pointers2 = [...this.pointers2];
  }

  nextPage() {
    const encodedValue = encodeURIComponent(this.activatedRouteProjectId);
    this.router.navigate([
      `projects/projects-setting/${customEncode(btoa(encodedValue))}/interpretation`,
    ]);
    this.sharedService.accessSideBarData(() => {
      // no-op: the sidebar refresh is fire-and-forget
    });
    this.sharedService.scenarioNameFunc('Intrepretation');
  }

  eolAction(event: any) {
    this.eolNavService.eolAction(
      event,
      event == 'fill-data-list' ? this.incorrectFilling : this.inValidScenarios,
      this.activatedRouteProjectId,
      scenario =>
        this.eolNavService.navigateToScenario(scenario, this.activatedRouteProjectId, event)
    );
  }
}
